import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useMemo, useState } from 'react'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import PersonRoundedIcon from '@mui/icons-material/PersonRounded'
import GroupAddRoundedIcon from '@mui/icons-material/GroupAddRounded'
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded'
import TranslateRoundedIcon from '@mui/icons-material/TranslateRounded'
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded'
import KeyRoundedIcon from '@mui/icons-material/KeyRounded'
import { updateProfile as updateFirebaseAuthProfile } from 'firebase/auth'
import { RailChipTabs, Select, StatusChip, TextInput } from '../Common'
import { Button } from '../Common/Button'
import { Pressable } from '../Common/Pressable'
import { JoinWithCodeForm } from '../JoinCodes/JoinWithCodeForm'

const MotionPressable = motion.create(Pressable)
import timeliningLogo from '../../assets/logo.png'
import { firebaseAuth } from '../../services/firebase'
import {
  collection,
  doc,
  endAt,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  startAt,
  where,
} from 'firebase/firestore'
import { useAuth } from '../../context/useAuth'
import { useClientPreferences } from '../../context/useClientPreferences'
import { useWorkspace } from '../../context/useWorkspace'
import { requestWorkspaceSwitch } from '../../services/workspaceSwitchService'
import { safeWorkspaceLanding } from '../../services/workspaceSwitch/switchLanding'
import { firestoreDb } from '../../services/firebase'
import type { ClientUserPreferences } from '../../types/schema'
import type { Workspace } from '../../types/workspace'
import { isPersonalWorkspace, isTeamWorkspace } from '../../utils/workspaceKind'
import {
  normalizeMemberEmail,
} from '../../services/memberService'
import { InteractiveConstellation } from '../Common/InteractiveConstellation'
import { PendingInviteInbox } from '../Invites/PendingInviteInbox'
import { useMemberInvites } from '../../hooks/useMemberInvites'
import { WorkspaceSetupOnboarding, type WorkspaceSetupDraft } from './WorkspaceSetupOnboarding'
import { applyOnboardingSetup } from '../../services/workspaceOnboardingService'
import { detectLocaleCurrency } from '../../utils/localeCurrency'
import { markWelcomePending } from '../../utils/workspaceWelcome'
import { ClaimTransferCodeModal } from '../AccountTransfer/ClaimTransferCodeModal'
import { NlsLocaleSelect } from '../../nls/NlsLocaleSelect'
import { useNls } from '../../nls/nlsProvider'
import { NLS_LOCALE_LABELS, NLS_MAJOR_LOCALES } from '../../nls/localePolicy'
import type { NlsLocale } from '../../nls/nlsTypes'

type GateMode = 'PERSONAL' | 'TEAM'
type OnboardingTeamTab = 'INVITES' | 'DISCOVER' | 'CREATE'

type PublicWorkspaceResult = {
  id: string
  workspaceId: string
  name: string
  ownerId: string
}

const PROFILE_ONBOARDED_KEY = 'timelining-profile-onboarded'

const profileOnboardedKeyForUser = (uid: string | null | undefined) =>
  uid ? `${PROFILE_ONBOARDED_KEY}:${uid}` : PROFILE_ONBOARDED_KEY

const hasCompletedProfileOnboarding = (uid: string | null | undefined) => {
  try {
    return window.localStorage.getItem(profileOnboardedKeyForUser(uid)) === '1'
  } catch {
    return false
  }
}

const buildTimezoneList = (): string[] => {
  try {
    const zones = (Intl as unknown as { supportedValuesOf?: (input: string) => string[] })
      .supportedValuesOf?.('timeZone')
    if (zones && zones.length > 0) return zones
  } catch {
    /* fall through */
  }
  return [
    'UTC',
    'America/Los_Angeles', 'America/Denver', 'America/Chicago', 'America/New_York',
    'America/Sao_Paulo',
    'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Moscow',
    'Africa/Cairo', 'Africa/Johannesburg',
    'Asia/Dubai', 'Asia/Karachi', 'Asia/Kolkata', 'Asia/Bangkok',
    'Asia/Shanghai', 'Asia/Singapore', 'Asia/Tokyo', 'Asia/Seoul',
    'Australia/Sydney', 'Pacific/Auckland',
  ]
}

const getSetupThemePreference = (
  theme: WorkspaceSetupDraft['theme'],
): Partial<ClientUserPreferences> => {
  if (theme === 'DARK') return { themeMode: 'FIXED', theme: 'DARK' }
  if (theme === 'LIGHT') return { themeMode: 'FIXED', theme: 'LIGHT' }
  return { themeMode: 'SYSTEM' }
}

type OnboardingGateProps = {
  isSwitching?: boolean
  onCancel?: () => void
  initialMode?: GateMode
  onSetupStarting?: () => void
  onSetupFailed?: () => void
  onWorkspaceReady?: (workspace: Workspace) => void
}

export const OnboardingGate = ({
  isSwitching = false,
  onCancel,
  initialMode = 'PERSONAL',
  onSetupStarting,
  onSetupFailed,
  onWorkspaceReady,
}: OnboardingGateProps) => {
  const { t, locale, setLocale } = useNls()
  const { currentUser, logout } = useAuth()
  const { updatePreferences } = useClientPreferences()
  const {
    workspaces,
    checkWorkspaceSlugAvailability,
    createWorkspace,
    refreshWorkspaces,
    setAppMode,
    workspaceLoadingError,
  } = useWorkspace()

  const [isRetryingWorkspaces, setIsRetryingWorkspaces] = useState(false)
  const runRetry = async () => {
    if (isRetryingWorkspaces) return
    setIsRetryingWorkspaces(true)
    try {
      await refreshWorkspaces()
    } finally {
      setIsRetryingWorkspaces(false)
    }
  }

  const [claimModalOpen, setClaimModalOpen] = useState(false)
  const [showJoinCodeForm, setShowJoinCodeForm] = useState(false)

  // `PROFILE` = first-run name/language/timezone step;
  // `CHOOSE` = twin-hero-card landing; `TEAM` = expanded team panel.
  const [stage, setStage] = useState<'PROFILE' | 'CHOOSE' | 'TEAM'>(() => {
    const nonProfileStage: 'CHOOSE' | 'TEAM' = initialMode === 'TEAM' ? 'TEAM' : 'CHOOSE'
    if (isSwitching) return nonProfileStage
    return hasCompletedProfileOnboarding(currentUser?.uid) ? nonProfileStage : 'PROFILE'
  })
  const [teamTab, setTeamTab] = useState<OnboardingTeamTab>('INVITES')

  const [profileName, setProfileName] = useState(() => currentUser?.displayName?.trim() ?? '')
  const [profileTimezone, setProfileTimezone] = useState(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
    } catch {
      return 'UTC'
    }
  })
  const [isSavingProfile, setIsSavingProfile] = useState(false)

  const {
    invites: memberInvites,
    count: memberInviteCount,
    loading: invitesLoading,
    error: invitesError,
    retry: retryInvites,
  } = useMemberInvites(currentUser?.email)
  const pendingUnifiedInviteCount = memberInviteCount
  const [searchText, setSearchText] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [searchResults, setSearchResults] = useState<PublicWorkspaceResult[]>([])
  const [requestedWorkspaceIds, setRequestedWorkspaceIds] = useState<Set<string>>(new Set())
  const [acceptedWorkspaceIds, setAcceptedWorkspaceIds] = useState<Set<string>>(new Set())
  const [isCreateTeamOpen, setIsCreateTeamOpen] = useState(() => {
    return typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('currency') === 'picker'
  })
  const [isPersonalSetupOpen, setIsPersonalSetupOpen] = useState(false)
  const [teamName, setTeamName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isWorking, setIsWorking] = useState(false)
  const normalizedEmail = normalizeMemberEmail(currentUser?.email)

  const timezoneList = useMemo(() => buildTimezoneList(), [])
  const languageOptions = useMemo(
    () =>
      NLS_MAJOR_LOCALES.map((option) => ({
        value: option,
        label: NLS_LOCALE_LABELS[option].nativeLabel,
      })),
    [],
  )
  const timezoneOptions = useMemo(
    () =>
      timezoneList.map((tz) => ({
        value: tz,
        label: tz.replace(/_/g, ' '),
      })),
    [timezoneList],
  )

  const completeProfileStep = async () => {
    const nextName = profileName.trim()
    if (!nextName) {
      setError('Please enter your name to continue.')
      return
    }
    setIsSavingProfile(true)
    setError(null)
    try {
      if (currentUser) {
        const firebaseUser = firebaseAuth.currentUser
        await Promise.all([
          setDoc(
            doc(firestoreDb, 'users', currentUser.uid),
            {
              displayName: nextName,
              timezone: profileTimezone,
              locale,
              profileOnboardedAt: serverTimestamp(),
            },
            { merge: true },
          ),
          firebaseUser && firebaseUser.displayName !== nextName
            ? updateFirebaseAuthProfile(firebaseUser, { displayName: nextName }).catch(() => undefined)
            : Promise.resolve(),
        ])
      }
      try { window.localStorage.setItem(profileOnboardedKeyForUser(currentUser?.uid), '1') } catch { /* ignore */ }
      setStage('CHOOSE')
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Could not save your profile.')
    } finally {
      setIsSavingProfile(false)
    }
  }

  const personalWorkspace = useMemo(
    () => workspaces.find((workspace) => isPersonalWorkspace(workspace)),
    [workspaces],
  )
  const ownsPersonalWorkspace = useMemo(
    () => Boolean(currentUser && workspaces.some((workspace) =>
      !workspace.isPreview && workspace.ownerId === currentUser.uid && isPersonalWorkspace(workspace)
    )),
    [currentUser, workspaces],
  )
  const ownsTeamWorkspace = useMemo(
    () => Boolean(currentUser && workspaces.some((workspace) =>
      !workspace.isPreview && workspace.ownerId === currentUser.uid && isTeamWorkspace(workspace)
    )),
    [currentUser, workspaces],
  )
  const canCreatePersonalWorkspace = !ownsPersonalWorkspace
  const canCreateTeamWorkspace = !ownsTeamWorkspace
  const showPersonalSetupChoice = canCreatePersonalWorkspace
  const showTeamSetupChoice = !isSwitching || canCreateTeamWorkspace

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (!canCreateTeamWorkspace && teamTab === 'CREATE') setTeamTab('INVITES')
  }, [canCreateTeamWorkspace, teamTab])

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedSearch(searchText.trim().toLowerCase()), 260)
    return () => window.clearTimeout(timeout)
  }, [searchText])

  useEffect(() => {
    if (!normalizedEmail) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setAcceptedWorkspaceIds(new Set())
      return
    }

    const activeMembershipsQuery = query(
      collection(firestoreDb, 'workspace_members'),
      where('user_email', '==', normalizedEmail),
      where('status', '==', 'ACTIVE'),
    )

    const unsubscribe = onSnapshot(
      activeMembershipsQuery,
      (snapshot) => {
        void Promise.all(snapshot.docs
          .map(async (membershipDocument) => {
            const data = membershipDocument.data()
            const workspaceId = typeof data.workspaceId === 'string' ? data.workspaceId : ''
            if (!workspaceId) return ''
            const workspaceSnapshot = await getDoc(doc(firestoreDb, 'workspaces', workspaceId))
            const workspaceData = workspaceSnapshot.exists() ? (workspaceSnapshot.data() as Pick<Workspace, 'kind' | 'isPersonal'>) : null
            return isPersonalWorkspace(workspaceData) ? '' : workspaceId

          }))
          .then((workspaceIds) => setAcceptedWorkspaceIds(new Set(workspaceIds.filter(Boolean))))
      },
      (error) => {
        console.warn('[Firestore listener failed] Onboarding active workspace_members by user_email/status', error)
        setAcceptedWorkspaceIds(new Set())
      },
    )

    return unsubscribe
  }, [normalizedEmail])

  useEffect(() => {
    if (!debouncedSearch) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSearchResults([])
      return
    }

    const publicQuery = query(
      collection(firestoreDb, 'public_workspaces'),
      orderBy('searchName'),
      startAt(debouncedSearch),
      endAt(`${debouncedSearch}`),
    )

    const unsubscribe = onSnapshot(
      publicQuery,
      (snapshot) => {
        setSearchResults(snapshot.docs.map((resultDocument) => {
          const data = resultDocument.data()
          return {
            id: resultDocument.id,
            workspaceId: typeof data.workspaceId === 'string' ? data.workspaceId : resultDocument.id,
            name: typeof data.name === 'string' ? data.name : 'Team workspace',
            ownerId: typeof data.ownerId === 'string' ? data.ownerId : '',
          } satisfies PublicWorkspaceResult
        }))
      },
      (error) => {
        console.warn('[Firestore listener failed] Onboarding public_workspaces search', error)
        setSearchResults([])
      },
    )

    return unsubscribe
  }, [debouncedSearch])

  const launchPersonalWorkspace = async () => {
    if (!currentUser?.email) return

    setIsWorking(true)
    setError(null)

    try {
      if (personalWorkspace) {
        setAppMode('PERSONAL')
        void requestWorkspaceSwitch({
          toWorkspaceId: personalWorkspace.id,
          source: 'USER',
          landing: safeWorkspaceLanding(personalWorkspace, 'DASHBOARD'),
        })
        onWorkspaceReady?.(personalWorkspace)
        return
      }

      setIsPersonalSetupOpen(true)
    } catch (nextError) {
      console.error('[Onboarding] Could not launch Personal Space', nextError)
      setError(nextError instanceof Error ? nextError.message : 'Could not launch Personal Space.')
    } finally {
      setIsWorking(false)
    }
  }

  const completePersonalSetup = async (draft: WorkspaceSetupDraft) => {
    if (!currentUser?.email) return

    setIsWorking(true)
    setError(null)
    onSetupStarting?.()

    try {
      const workspace = await createWorkspace('Personal Space', 'PERSONAL', {
        currency: detectLocaleCurrency(),
      })
      await applyOnboardingSetup(workspace.id, draft, 'personal')

      const setupTheme = getSetupThemePreference(draft.theme)
      updatePreferences(setupTheme)
      markWelcomePending(currentUser?.uid, workspace.id)

      // createWorkspace already added the workspace + role locally and set it
      // active. Do NOT call refreshWorkspaces here: on a brand-new doc the
      // Firestore query can miss it (eventual consistency / index lag) and the
      // subsequent setWorkspaces([...]) would clobber the just-added workspace
      // right out of state, leaving needsOnboarding stuck at true and the setup
      // splash spinning forever.
      setAppMode('PERSONAL')
      void requestWorkspaceSwitch({
        toWorkspaceId: workspace.id,
        source: 'CREATED',
        landing: safeWorkspaceLanding(workspace, 'DASHBOARD'),
      })
      onWorkspaceReady?.(workspace)
      setIsPersonalSetupOpen(false)
    } catch (nextError) {
      console.error('[Onboarding] Could not create Personal Space', nextError)
      setError(nextError instanceof Error ? nextError.message : 'Could not create Personal Space.')
      onSetupFailed?.()
    } finally {
      setIsWorking(false)
    }
  }

  const completeSetupOnboarding = async (draft: WorkspaceSetupDraft) => {
    const nextName = draft.workspaceName.trim()
    if (!nextName) {
      setError('Enter a team workspace name.')
      return
    }

    setIsWorking(true)
    setError(null)
    onSetupStarting?.()

    try {
      const workspace = await createWorkspace(nextName, 'TEAM', {
        slug: draft.workspaceSlug,
        isPublic: draft.isPublic,
        currency: draft.currency,
      })
      await applyOnboardingSetup(workspace.id, draft, 'team')

      const setupTheme = getSetupThemePreference(draft.theme)
      updatePreferences(setupTheme)
      markWelcomePending(currentUser?.uid, workspace.id)

      // Same race as Personal — createWorkspace already updated local state, so
      // skip refreshWorkspaces here to avoid clobbering the fresh workspace.
      setAppMode('TEAM')
      void requestWorkspaceSwitch({
        toWorkspaceId: workspace.id,
        source: 'CREATED',
        landing: safeWorkspaceLanding(workspace, 'DASHBOARD'),
      })
      onWorkspaceReady?.(workspace)
      setIsCreateTeamOpen(false)
    } catch (nextError) {
      console.error('[Onboarding] Could not create team workspace', nextError)
      setError(nextError instanceof Error ? nextError.message : 'Could not create team workspace.')
      onSetupFailed?.()
    } finally {
      setIsWorking(false)
    }
  }

  const requestToJoin = async (target: PublicWorkspaceResult) => {
    if (!currentUser?.email) return

    setError(null)

    try {
      await setDoc(
        doc(firestoreDb, 'join_requests', `${target.workspaceId}_${currentUser.uid}`),
        {
          workspaceId: target.workspaceId,
          applicantUid: currentUser.uid,
          applicantEmail: normalizedEmail,
          status: 'PENDING',
          requestedAt: serverTimestamp(),
        },
        { merge: true },
      )
      setRequestedWorkspaceIds((current) => new Set(current).add(target.workspaceId))
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Could not send request.')
    }
  }

  const enterWorkspace = async (workspaceId: string) => {
    setIsWorking(true)
    setError(null)

    try {
      await refreshWorkspaces(workspaceId, 'TEAM')
      const target = workspaces.find((w) => w.id === workspaceId)
      setAppMode('TEAM')
      void requestWorkspaceSwitch({
        toWorkspaceId: workspaceId,
        source: 'USER',
        landing: safeWorkspaceLanding(target, 'DASHBOARD'),
      })
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Could not enter workspace.')
    } finally {
      setIsWorking(false)
    }
  }

  return (
    <main className="onboardingGate" aria-label={t('NLSOnboardingAriaWorkspaceSetup')}>
      <InteractiveConstellation
        className="onboardingGate__constellation"
        density={2000}
        linkDistance={120}
        interactionRadius={180}
        intensity={0.9}
        opacity={0.72}
      />
      <div className="onboardingGate__vignette" aria-hidden="true" />

      <section className="onboardingGate__deck">
        <header className="onboardingGate__topBar">
          <div className="onboardingGate__brand">
            <img src={timeliningLogo} alt="" aria-hidden="true" />
            <span>{t('NLSAuthBrandName')}</span>
          </div>
          <div className="onboardingGate__topActions">
            <NlsLocaleSelect className="onboardingLanguageSelect" compact />
            {!isSwitching && (
              <Button
                variant="tertiary"
                size="sm"
                className="onboardingGate__topSignOutBtn"
                icon={<LogoutRoundedIcon />}
                onClick={() => { void logout() }}
              >
                {t('NLSCommonActionSignOut')}
              </Button>
            )}
            {stage === 'TEAM' && (
              <Button
                variant="tertiary"
                size="sm"
                icon={<ArrowBackRoundedIcon />}
                onClick={() => setStage('CHOOSE')}
              >
                {t('NLSCommonActionBack')}
              </Button>
            )}
            {isSwitching && (
              <Button
                variant="tertiary"
                size="sm"
                icon={<CloseRoundedIcon />}
                onClick={onCancel}
              >
                {t('NLSCommonActionCancel')}
              </Button>
            )}
          </div>
        </header>

        <AnimatePresence mode="wait" initial={false}>
          {stage === 'PROFILE' ? (
            <motion.div
              key="profile"
              className="onboardingGate__stage onboardingGate__stage--profile"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.28 }}
            >
              <div className="onboardingGate__intro">
                <p className="onboardingGate__eyebrow">Welcome to Timelining</p>
                <h1 className="onboardingGate__title">A few last details.</h1>
                <p className="onboardingGate__lede">
                  Tell us how we should address you, then set your language and timezone. You can change these anytime.
                </p>
              </div>

              <div className="onboardingProfile__grid">
                <label className="onboardingProfile__field">
                  <span className="onboardingProfile__fieldLabel">Full name</span>
                  <TextInput
                    value={profileName}
                    onChange={setProfileName}
                    placeholder="Your name"
                    leadingIcon={<PersonRoundedIcon fontSize="small" />}
                    autoFocus
                  />
                </label>

                <div className="onboardingProfile__field">
                  <span className="onboardingProfile__fieldLabel">Language</span>
                  <Select
                    className="onboardingProfile__dropdown"
                    trigger="labeledPill"
                    label={<TranslateRoundedIcon fontSize="small" aria-hidden />}
                    value={locale}
                    options={languageOptions}
                    onChange={(next) => setLocale(next as NlsLocale)}
                    ariaLabel={t('NLSCommonLanguageAriaLabel')}
                  />
                </div>

                <div className="onboardingProfile__field">
                  <span className="onboardingProfile__fieldLabel">Timezone</span>
                  <Select
                    className="onboardingProfile__dropdown"
                    trigger="labeledPill"
                    label={<AccessTimeRoundedIcon fontSize="small" aria-hidden />}
                    value={profileTimezone}
                    options={timezoneOptions}
                    onChange={setProfileTimezone}
                    ariaLabel="Choose timezone"
                    searchable
                  />
                </div>
              </div>

              <div className="onboardingProfile__actions">
                <Button
                  variant="primary"
                  size="lg"
                  trailingIcon={<ArrowForwardRoundedIcon />}
                  onClick={() => void completeProfileStep()}
                  disabled={isSavingProfile || !profileName.trim()}
                >
                  {isSavingProfile ? 'Saving…' : 'Continue'}
                </Button>
              </div>
            </motion.div>
          ) : stage === 'CHOOSE' ? (
            <motion.div
              key="choose"
              className="onboardingGate__stage"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.28 }}
            >
              <div className="onboardingGate__intro">
                <p className="onboardingGate__eyebrow">{isSwitching ? t('NLSOnboardingEyebrowWorkspaceSetup') : t('NLSOnboardingEyebrowNewSpace')}</p>
                <h1 className="onboardingGate__title">
                  {isSwitching ? t('NLSOnboardingTitleSwitchSetup') : t('NLSOnboardingTitleBegin')}
                </h1>
                <p className="onboardingGate__lede">
                  {t('NLSOnboardingLedeOwnership')}
                </p>
              </div>

              {pendingUnifiedInviteCount > 0 && (
                <motion.div
                  className="onboardingGate__inviteBanner"
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  role="region"
                  aria-label={t('NLSOnboardingInviteBannerRegionAriaLabel')}
                >
                  <div className="onboardingGate__inviteBannerContent">
                    <GroupAddRoundedIcon className="onboardingGate__inviteBannerIcon" />
                    <div>
                      <strong className="onboardingGate__inviteBannerTitle">
                        {t(pendingUnifiedInviteCount > 1 ? 'NLSOnboardingInvitesWaiting' : 'NLSOnboardingInviteWaiting', { count: pendingUnifiedInviteCount })}
                      </strong>
                      <p className="onboardingGate__inviteBannerHint">
                        {t('NLSOnboardingInviteBannerHint')}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => {
                      setStage('TEAM')
                      setTeamTab('INVITES')
                    }}
                  >
                    {t('NLSOnboardingTabInvites')} →
                  </Button>
                </motion.div>
              )}

              {showJoinCodeForm ? (
                <div className="onboardingGate__joinCodeSection p-6 rounded-2xl bg-[var(--surface-card)] border border-[var(--border)] max-w-lg mx-auto w-full my-4">
                  <JoinWithCodeForm
                    onClaimed={async (claimed) => {
                      const refreshed = await refreshWorkspaces(claimed.workspaceId)
                      const loaded = (Array.isArray(refreshed) ? refreshed : workspaces).find(
                        (w) => w.id === claimed.workspaceId
                      )
                      if (onWorkspaceReady && loaded) {
                        onWorkspaceReady(loaded)
                      } else {
                        void requestWorkspaceSwitch({
                          toWorkspaceId: claimed.workspaceId,
                          source: 'JOINED',
                          landing: safeWorkspaceLanding(loaded, 'DASHBOARD'),
                        })
                      }
                    }}
                    onCancel={() => setShowJoinCodeForm(false)}
                  />
                </div>
              ) : (
                <div className="onboardingGate__joinCodeCard mb-3">
                  <HeroCard
                    variant="team"
                    eyebrow={t('NLSJoinWithCodeAction')}
                    title={t('NLSOnboardingHaveJoinCodeTitle')}
                    description={t('NLSOnboardingHaveJoinCodeDescription')}
                    cta={t('NLSOnboardingActionEnterJoinCode')}
                    glyph={<KeyRoundedIcon sx={{ fontSize: 32, color: 'var(--accent)' }} />}
                    onClick={() => setShowJoinCodeForm(true)}
                    disabled={isWorking}
                  />
                </div>
              )}

              <div className="onboardingGate__heroGrid">
                {showPersonalSetupChoice && (
                  <HeroCard
                    variant="personal"
                    eyebrow={t('NLSOnboardingPersonalEyebrow')}
                    title={t('NLSOnboardingPersonalTitle')}
                    description={t('NLSOnboardingPersonalDescription')}
                    cta={t('NLSOnboardingActionLaunchPersonal')}
                    onClick={() => void launchPersonalWorkspace()}
                    disabled={isWorking}
                  />
                )}
                {showTeamSetupChoice && (
                  <HeroCard
                    variant="team"
                    eyebrow={pendingUnifiedInviteCount > 0
                      ? t(pendingUnifiedInviteCount > 1 ? 'NLSOnboardingInvitesWaiting' : 'NLSOnboardingInviteWaiting', { count: pendingUnifiedInviteCount })
                      : canCreateTeamWorkspace
                        ? t('NLSOnboardingTeamEyebrowCreate')
                        : t('NLSOnboardingTeamEyebrowAccess')}
                    title={canCreateTeamWorkspace ? t('NLSOnboardingTeamTitleCreate') : t('NLSOnboardingTeamTitleAccess')}
                    description={pendingUnifiedInviteCount > 0
                      ? t('NLSOnboardingTeamDescriptionInvitesWaiting')
                      : canCreateTeamWorkspace
                        ? t('NLSOnboardingTeamDescriptionCreate')
                        : t('NLSOnboardingTeamDescriptionAccess')}
                    cta={pendingUnifiedInviteCount > 0
                      ? t('NLSOnboardingTabInvites')
                      : canCreateTeamWorkspace
                        ? t('NLSOnboardingActionExploreTeam')
                        : t('NLSOnboardingActionViewInvitesTeams')}
                    badge={pendingUnifiedInviteCount > 0 ? t(pendingUnifiedInviteCount > 1 ? 'NLSOnboardingInvitesWaiting' : 'NLSOnboardingInviteWaiting', { count: pendingUnifiedInviteCount }) : undefined}
                    onClick={() => {
                      setStage('TEAM')
                      if (pendingUnifiedInviteCount > 0) setTeamTab('INVITES')
                    }}
                    disabled={isWorking}
                  />
                )}
              </div>

              {currentUser && !currentUser.isAnonymous && (
                <div className="onboardingGate__transferRow flex justify-center mt-3 mb-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setClaimModalOpen(true)}
                  >
                    {t('NLSAccountTransferHaveCodeButton')}
                  </Button>
                </div>
              )}

              {!showPersonalSetupChoice && !showTeamSetupChoice && (
                <p className="onboardingGate__footnote">
                  {t('NLSOnboardingFootnoteNoSetups')}
                </p>
              )}
              {(showPersonalSetupChoice || showTeamSetupChoice) && (
                <p className="onboardingGate__footnote">
                  {t('NLSOnboardingFootnoteOwnershipCap')}
                </p>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="team"
              className="onboardingGate__stage"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.28 }}
            >
              <div className="onboardingGate__intro onboardingGate__intro--tight">
                <p className="onboardingGate__eyebrow">{t('NLSOnboardingTeamStageEyebrow')}</p>
                <h2 className="onboardingGate__title onboardingGate__title--sm">
                  {t('NLSOnboardingTeamStageTitle')}
                </h2>
              </div>

              <RailChipTabs
                orientation="horizontal"
                ariaLabel={t('NLSOnboardingTeamStageTitle')}
                value={teamTab}
                onChange={(key) => {
                  if (key === 'CREATE' && !canCreateTeamWorkspace) return
                  setTeamTab(key as 'INVITES' | 'DISCOVER' | 'CREATE')
                }}
                items={[
                  {
                    key: 'INVITES',
                    label: t('NLSOnboardingTabInvites'),
                    count: pendingUnifiedInviteCount || undefined,
                  },
                  {
                    key: 'DISCOVER',
                    label: t('NLSOnboardingTabDiscover'),
                  },
                  {
                    key: 'CREATE',
                    label: t('NLSOnboardingTabCreateNew'),
                    disabled: !canCreateTeamWorkspace,
                  },
                ]}
              />

              <div className="onboardingGate__teamPanel">
                <AnimatePresence mode="wait" initial={false}>
                  {teamTab === 'INVITES' && (
                    <motion.div
                      key="invites"
                      className="onboardingGate__tabBody"
                      initial={{ opacity: 0, x: 12 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -12 }}
                      transition={{ duration: 0.2 }}
                    >
                      <PendingInviteInbox
                        roleHint="MEMBER"
                        compact
                        invites={memberInvites}
                        loading={invitesLoading}
                        error={invitesError ? (invitesError.message || t('NLSOnboardingInvitesLoadError')) : null}
                        onRetry={retryInvites}
                        onAccepted={(invite) => {
                          if (invite.workspaceId) {
                            void enterWorkspace(invite.workspaceId)
                          }
                        }}
                      />
                    </motion.div>
                  )}

                  {teamTab === 'DISCOVER' && (
                    <motion.div
                      key="discover"
                      className="onboardingGate__tabBody"
                      initial={{ opacity: 0, x: 12 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -12 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className="onboardingGate__searchField">
                        <TextInput
                          type="search"
                          value={searchText}
                          onChange={setSearchText}
                          placeholder={t('NLSOnboardingSearchPublicTeamsPlaceholder')}
                          leadingIcon={<SearchRoundedIcon fontSize="small" />}
                          autoFocus
                        />
                      </div>
                      {searchResults.length === 0 ? (
                        <EmptyState
                          title={debouncedSearch ? 'No public teams match that name' : 'Discover public teams'}
                          hint={debouncedSearch ? 'Try a different search term.' : 'Type a team name to see workspaces that opened themselves to the world.'}
                        />
                      ) : (
                        <ul className="onboardingGate__list">
                          {searchResults.map((result) => (
                            <li key={result.id} className="onboardingGate__row">
                              <div className="onboardingGate__rowMain">
                                <strong>{result.name}</strong>
                                <span>Public team workspace</span>
                              </div>
                              <div className="onboardingGate__rowActions">
                                {acceptedWorkspaceIds.has(result.workspaceId) ? (
                                  <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => void enterWorkspace(result.workspaceId)}
                                    disabled={isWorking}
                                  >
                                    {t('NLSOnboardingActionEnter')}
                                  </Button>
                                ) : (
                                  <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => void requestToJoin(result)}
                                    disabled={requestedWorkspaceIds.has(result.workspaceId)}
                                  >
                                    {requestedWorkspaceIds.has(result.workspaceId) ? t('NLSOnboardingActionRequested') : t('NLSOnboardingActionRequestAccess')}
                                  </Button>
                                )}
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </motion.div>
                  )}

                  {teamTab === 'CREATE' && (
                    <motion.div
                      key="create"
                      className="onboardingGate__tabBody onboardingGate__tabBody--createTab"
                      initial={{ opacity: 0, x: 12 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -12 }}
                      transition={{ duration: 0.2 }}
                    >
                      {canCreateTeamWorkspace ? (
                        <div className="onboardingGate__createPanel">
                          <div className="onboardingGate__createArt" aria-hidden="true">
                            <span className="onboardingGate__seal">◇</span>
                          </div>
                          <div className="onboardingGate__createCopy">
                            <p className="onboardingGate__eyebrow">Found a workspace</p>
                            <h3>Set the stage for your team.</h3>
                            <p>
                              We&rsquo;ll walk you through a short, guided setup — role, focus, and vibe — then open your
                              Team workspace ready to invite the rest of the crew.
                            </p>
                            <Button
                              variant="primary"
                              size="lg"
                              trailingIcon={<ArrowForwardRoundedIcon />}
                              onClick={() => setIsCreateTeamOpen(true)}
                            >
                              Begin guided setup
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <EmptyState
                          title="Team workspace already owned"
                          hint="This account can own one Team workspace. Use Invites or Discover to access teams owned by others."
                        />
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {isSwitching && !personalWorkspace && (
                <Button
                  variant="tertiary"
                  size="sm"
                  trailingIcon={<ArrowForwardRoundedIcon />}
                  onClick={() => void launchPersonalWorkspace()}
                  disabled={isWorking}
                >
                  Or launch a Personal Space instead
                </Button>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {(error || workspaceLoadingError) && <p className="onboardingGate__error">{error || workspaceLoadingError}</p>}

        {!isSwitching && (
          <div className="onboardingGate__retryLine">
            <span className="onboardingGate__retryHint">Not seeing your workspaces?</span>
            <Button
              variant="link"
              size="sm"
              onClick={() => { void runRetry() }}
              disabled={isRetryingWorkspaces}
            >
              {isRetryingWorkspaces ? t('NLSCommonActionRetrying') : t('NLSCommonActionRetryLoadingWorkspaces')}
            </Button>
          </div>
        )}
      </section>

      {isCreateTeamOpen && (
        <div
          className="setupOnboardingOverlay"
          // eslint-disable-next-line no-restricted-syntax -- full-screen workspace setup overlay
          role="dialog"
          aria-modal="true"
          aria-label={t('NLSOnboardingDialogTeamSetup')}
        >
          <WorkspaceSetupOnboarding
            initialDraft={teamName ? { workspaceName: teamName } : undefined}
            isCompleting={isWorking}
            onCancel={() => setIsCreateTeamOpen(false)}
            onCheckWorkspaceSlug={checkWorkspaceSlugAvailability}
            onComplete={(draft) => {
              setTeamName(draft.workspaceName)
              void completeSetupOnboarding(draft)
            }}
          />
        </div>
      )}
      {isPersonalSetupOpen && (
        <div
          className="setupOnboardingOverlay"
          // eslint-disable-next-line no-restricted-syntax -- full-screen workspace setup overlay
          role="dialog"
          aria-modal="true"
          aria-label={t('NLSOnboardingDialogPersonalSetup')}
        >
          <WorkspaceSetupOnboarding
            setupKind="personal"
            isCompleting={isWorking}
            onCancel={() => setIsPersonalSetupOpen(false)}
            onComplete={(draft) => void completePersonalSetup(draft)}
          />
        </div>
      )}
      {claimModalOpen && (
        <ClaimTransferCodeModal
          open={claimModalOpen}
          onClose={() => setClaimModalOpen(false)}
        />
      )}
    </main>
  )
}

type HeroCardProps = {
  variant: 'personal' | 'team' | 'join-code'
  eyebrow: string
  title: string
  description: string
  cta: string
  badge?: string
  glyph?: React.ReactNode
  onClick: () => void
  disabled?: boolean
}

const HeroCard = ({ variant, eyebrow, title, description, cta, badge, glyph, onClick, disabled }: HeroCardProps) => (
  <MotionPressable
    className={`onboardingHero onboardingHero--${variant}`}
    onClick={onClick}
    disabled={disabled}
    whileHover={disabled ? undefined : { y: -4 }}
    whileTap={disabled ? undefined : { y: -1, scale: 0.995 }}
    transition={{ type: 'spring', stiffness: 260, damping: 22 }}
  >
    <span className="onboardingHero__sheen" aria-hidden="true" />
    <span className="onboardingHero__glyph" aria-hidden="true">
      {glyph ?? (variant === 'personal' ? <PersonalGlyph /> : <TeamGlyph />)}
    </span>
    <span className="onboardingHero__eyebrow">{eyebrow}</span>
    <span className="onboardingHero__title">{title}</span>
    <span className="onboardingHero__desc">{description}</span>
    {badge && <StatusChip tone="info" size="sm" className="onboardingHero__badge">{badge}</StatusChip>}
    <span className="onboardingHero__cta">
      {cta} <span aria-hidden="true">→</span>
    </span>
  </MotionPressable>
)

const PersonalGlyph = () => (
  <svg viewBox="0 0 48 48" width="48" height="48" fill="none">
    <circle cx="24" cy="24" r="20" stroke="currentColor" strokeWidth="1.4" opacity="0.35" />
    <circle cx="24" cy="24" r="12" stroke="currentColor" strokeWidth="1.4" opacity="0.7" />
    <circle cx="24" cy="24" r="4" fill="currentColor" />
  </svg>
)

const TeamGlyph = () => (
  <svg viewBox="0 0 48 48" width="48" height="48" fill="none">
    <circle cx="16" cy="20" r="6" stroke="currentColor" strokeWidth="1.4" />
    <circle cx="32" cy="20" r="6" stroke="currentColor" strokeWidth="1.4" />
    <circle cx="24" cy="34" r="6" stroke="currentColor" strokeWidth="1.4" />
    <path d="M18 24 L26 30 M30 24 L22 30" stroke="currentColor" strokeWidth="1.2" opacity="0.55" />
  </svg>
)

const EmptyState = ({ title, hint }: { title: string; hint: string }) => (
  <div className="onboardingGate__empty">
    <span className="onboardingGate__emptyGlyph" aria-hidden="true">
      <svg viewBox="0 0 40 40" width="34" height="34" fill="none">
        <circle cx="20" cy="20" r="14" stroke="currentColor" strokeWidth="1.2" opacity="0.4" />
        <circle cx="20" cy="20" r="6" stroke="currentColor" strokeWidth="1.2" opacity="0.7" />
      </svg>
    </span>
    <strong>{title}</strong>
    <span>{hint}</span>
  </div>
)
